import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { 
  Settings as SettingsIcon, Save, Loader2, AlertTriangle, Building2, RefreshCw
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { token } = useAuth();
  const [config, setConfig] = useState({
    tolerance_montant: 0.02,
    seuil_auto_validation: 90,
    fenetre_dates_jours: 5,
    emails_alertes: ["admin@betaye.fr"],
    alertes_actives: true
  });
  const [bridgeUser, setBridgeUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setLoading(true);
      const [configRes, userRes] = await Promise.all([
        axios.get(`${API}/api/bridge-v2/config`, { headers }),
        axios.get(`${API}/api/bridge-v2/user-status`, { headers }).catch(() => ({ data: null }))
      ]);
      
      if (configRes.data) {
        setConfig(prev => ({ ...prev, ...configRes.data }));
      }
      setBridgeUser(userRes.data);
    } catch (err) {
      console.error("Erreur chargement config:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setSaving(true);
      await axios.put(`${API}/api/bridge-v2/config`, config, { headers });
      setMessage({ type: "success", text: "Configuration sauvegardée" });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      setMessage({ type: "error", text: "Erreur lors de la sauvegarde" });
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setConnecting(true);
      const res = await axios.post(`${API}/api/bridge-v2/connect`, {
        user_email: "admin@betaye.fr"
      }, { headers });
      
      if (res.data.connect_url) {
        // Ouvrir dans une nouvelle fenêtre
        window.open(res.data.connect_url, '_blank', 'width=600,height=700');
        setMessage({ type: "info", text: "Complétez la connexion dans la fenêtre ouverte" });
      }
    } catch (err) {
      console.error("Erreur connexion Bridge:", err);
      setMessage({ type: "error", text: "Erreur lors de la connexion à Bridge" });
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Paramètres</h1>
        <p className="text-slate-500 mt-1">Configuration du rapprochement bancaire</p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Connexion Bridge */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Connexion Bridge API
          </CardTitle>
          <CardDescription>
            Connectez votre compte bancaire via Bridge pour synchroniser les transactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bridgeUser ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Compte connecté</p>
                  <p className="text-sm text-slate-500">{bridgeUser.external_user_id || bridgeUser.bridge_user_uuid}</p>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-700">Actif</Badge>
            </div>
          ) : (
            <div className="text-center py-4">
              <AlertTriangle className="w-12 h-12 mx-auto text-orange-500 mb-3" />
              <p className="font-medium text-slate-900 mb-2">Aucun compte connecté</p>
              <p className="text-sm text-slate-500 mb-4">
                Connectez votre banque pour commencer le rapprochement automatique
              </p>
              <Button onClick={handleConnect} disabled={connecting}>
                {connecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Building2 className="w-4 h-4 mr-2" />
                )}
                Connecter ma banque
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Règles de matching */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5" />
            Règles de rapprochement
          </CardTitle>
          <CardDescription>
            Paramètres pour le matching automatique des transactions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="tolerance">Tolérance montant (€)</Label>
              <Input
                id="tolerance"
                type="number"
                step="0.01"
                value={config.tolerance_montant}
                onChange={(e) => setConfig({ ...config, tolerance_montant: parseFloat(e.target.value) || 0 })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Écart maximum autorisé entre le montant de la transaction et de la facture
              </p>
            </div>

            <div>
              <Label htmlFor="seuil">Seuil auto-validation (%)</Label>
              <Input
                id="seuil"
                type="number"
                min="0"
                max="100"
                value={config.seuil_auto_validation}
                onChange={(e) => setConfig({ ...config, seuil_auto_validation: parseInt(e.target.value) || 0 })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Score minimum pour valider automatiquement un match
              </p>
            </div>

            <div>
              <Label htmlFor="fenetre">Fenêtre dates (jours)</Label>
              <Input
                id="fenetre"
                type="number"
                min="0"
                max="30"
                value={config.fenetre_dates_jours}
                onChange={(e) => setConfig({ ...config, fenetre_dates_jours: parseInt(e.target.value) || 0 })}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Tolérance en jours entre mois adjacents
              </p>
            </div>
          </div>

          <div className="border-t pt-6">
            <div className="flex items-center justify-between">
              <div>
                <Label>Alertes email</Label>
                <p className="text-sm text-slate-500">
                  Recevoir des notifications pour les transactions en attente
                </p>
              </div>
              <Switch
                checked={config.alertes_actives}
                onCheckedChange={(checked) => setConfig({ ...config, alertes_actives: checked })}
              />
            </div>

            {config.alertes_actives && (
              <div className="mt-4">
                <Label htmlFor="emails">Emails de notification</Label>
                <Input
                  id="emails"
                  value={config.emails_alertes?.join(', ') || ''}
                  onChange={(e) => setConfig({ 
                    ...config, 
                    emails_alertes: e.target.value.split(',').map(e => e.trim()).filter(Boolean)
                  })}
                  placeholder="email1@exemple.fr, email2@exemple.fr"
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bouton sauvegarder */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Sauvegarder
        </Button>
      </div>
    </div>
  );
}
